import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { metrajGoster } from '../lib/olcu';
import { metrajYazi } from '@mod/domain';
import { api } from '../lib/api';
import { useOturum } from '../lib/oturum';
import {
  KameraIkon,
  KapatIkon,
} from '../lib/ikon';

/**
 * ŞŞ-05 — GÜNLÜK RAPOR (devir dokumani Bolum 6.8, prototip `vRapor`).
 *
 * PROJENIN DEGER ONERMESI BU EKRAN. Bugun her sabah elle Excel'e giriliyor;
 * artik gunun tiklerinden derleniyor.
 *
 * ⚠️ OTOMATIK GONDERILMEZ — sef onayiyla gider.
 */

interface RaporSatir {
  siparisNo: string;
  /**
   * İskele onay no — aynı siparişte birden çok iskele olabiliyor.
   *
   * ⚠️ OPSİYONEL: 07.08.2026 ÖNCESİ gönderilmiş raporların mühürlü anlık
   *    görüntüsünde bu alan YOKTUR. Arşivden açılan eski bir raporda
   *    `undefined` gelir ve "—" gösterilir; mühür değiştirilmez.
   */
  iskeleOnayNo?: string;
  talepEden: string;
  talepEdenBirim: string;
  tanim: string;
  unite: string;
  /** İKİ AYRI TOPLAM — karışık iş tek sayıya indirgenmez (02.08.2026). */
  metrajM3: number;
  metrajM2: number;
  /** m³ + m² × 4 — tek sayı gerektiğinde. */
  esdegerM3: number;
  /** Gercek raporda `3*2` bicimi — kisi x saat, hesaplanmis sayi degil. */
  harcanan: string;
  fotoNo: string;
}

interface Rapor {
  tarih: string;
  gonderildiMi: boolean;
  gonderimZamani: string | null;
  isveren: string;
  santiyeSefi: string;
  kurulumlar: RaporSatir[];
  sokumler: RaporSatir[];
  /**
   * ⚠️ ARTIK NESNE, tek sayı DEĞİL (02.08.2026 çift birim geçişi).
   *    Günlük rapor iki metrajı ayrı gösteriyor; eşdeğer yalnız aylıkta.
   */
  toplamKurulum: { m3: number; m2: number; esdegerM3: number };
  toplamSokum: { m3: number; m2: number; esdegerM3: number };
  aylikKurulum: number;
  aylikSokum: number;
  fotolar: { fotoNo: number; isEmriNo: string | null; unite: string; step: string }[];
  eksikAlanlar: string[];
  /** Personel alanı boşsa arayüzün önereceği metin — künyeye yazılmaz. */
  personelOneri: string;
  /** true → mailde giden dondurulmus icerik, canli derleme degil. */
  arsiv: boolean;
  kunye: Kunye;
  /** Rutin kontrol kayitlarindan TURETILIR — elle girilmez. */
  uniteKontrol: { unite: string; adet: number }[];
  haftalikGuncelleme: { tarih: string; adet: number }[];
  haftalikToplam: number;
}

interface Ekipman {
  ad: string;
  adet: number;
}

interface Kunye {
  hava: string;
  sicaklik: number | null;
  personel: number | null;
  ekipman: Ekipman[];
  malzemeTonaj: number | null;
  dunkuPersonel: string;
  bugunkuPersonel: string;
  notlar: string;
}

/** Yerel gun — `toISOString()` UTC'ye kaydirip geceyarisi civarinda gunu sasirtiyor. */
const gunAnahtari = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const BUGUN = gunAnahtari(new Date());

const fmt = (n: number) => n.toFixed(2).replace('.', ',');
const ALAN_ADI: Record<string, string> = {
  hava: 'hava durumu',
  sicaklik: 'sıcaklık',
  personel: 'personel sayısı',
  ekipman: 'ekipman parkı',
  malzemeTonaj: 'malzeme tonajı',
  dunkuPersonel: 'önceki günün personeli',
  bugunkuPersonel: 'bugünkü personel',
  // `notlar` BİLEREK YOK: eksik alan uyarısı çıkarmaz, doldurulması zorunlu değil.
};

const gunEtiket = (g: string) =>
  new Date(`${g}T12:00:00`).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' });

export function Rapor() {
  const { ben } = useOturum();
  /** Müşteri yetkilisi raporu OKUR; künye düzenleme ve gönderim ona kapalı. */
  const musteri = ben?.musteri ?? false;
  const [gun, setGun] = useState(BUGUN);
  const [canli, setCanli] = useState(false);
  const [r, setR] = useState<Rapor | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [gonderiliyor, setGonderiliyor] = useState(false);
  const [mesaj, setMesaj] = useState<string | null>(null);

  const yukle = useCallback(async () => {
    setHata(null);
    try {
      setR(await api.get<Rapor>(`/rapor?tarih=${gun}${canli ? '&canli=1' : ''}`));
    } catch (e) {
      setHata(e instanceof Error ? e.message : 'Yüklenemedi');
    }
  }, [gun, canli]);

  useEffect(() => {
    void yukle();
  }, [yukle]);

  /** Gün değişince "canlı derleme" isteği sıfırlanır — o gün için ayrıca istenmeli. */
  function gunSec(yeni: string) {
    if (yeni > BUGUN) return; // gelecek gün anlamsız: rapor GÜNÜN tiklerinden derlenir
    setMesaj(null);
    setCanli(false);
    setGun(yeni);
  }

  function kaydir(gunSayisi: number) {
    const d = new Date(`${gun}T12:00:00`);
    d.setDate(d.getDate() + gunSayisi);
    gunSec(gunAnahtari(d));
  }

  async function gonder() {
    setGonderiliyor(true);
    setHata(null);
    try {
      // ⚠️ SEÇİLİ GÜN gönderilir. Tarih yollanmazsa sunucu BUGÜNÜ varsayar —
      //    geçmiş bir günü açıp "gönder" demek yanlış raporu maillerdi.
      const s = await api.post<{ ok: boolean; hata?: string; alici: number }>('/rapor/gonder', {
        tarih: gun,
      });
      setMesaj(
        s.ok
          ? `Rapor ${s.alici} ilgili kişiye gönderildi ✓`
          : `Gönderilemedi: ${s.hata ?? 'bilinmeyen hata'}`,
      );
      setCanli(false);
      await yukle();
    } catch (e) {
      setHata(e instanceof Error ? e.message : 'Gönderilemedi');
    } finally {
      setGonderiliyor(false);
    }
  }

  if (hata && !r)
    return <p className="m-6 rounded-(--radius-mod) bg-crit/10 p-4 text-sm text-crit">{hata}</p>;
  if (!r) return <p className="m-6 text-sm text-wait">Yükleniyor…</p>;

  return (
    <div className="mx-auto max-w-5xl p-4 lg:p-6">
      {/* Başlık bloğu */}
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3 rounded-(--radius-mod) border border-line bg-surface shadow-(--shadow-mod) p-4">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-(--radius-mod) bg-navy font-bold text-white">
            M
          </div>
          <div>
            <h2 className="font-semibold text-navy">
              Günlük Rapor —{' '}
              {new Date(r.tarih).toLocaleDateString('tr-TR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              })}
            </h2>
            <p className="text-sm text-wait">
              Mod İskele → {ben?.santiye.musteriAd ?? 'müşteri'} · gün verisinden otomatik
              derlenir, şef onayıyla gider
            </p>
          </div>
        </div>

        {/* GÜN SEÇİCİ — geçmiş günler açılabilir. İleri gün yok: rapor o günün
            tiklerinden derlenir, henüz olmamış bir günün raporu da yoktur. */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => kaydir(-1)}
            title="Önceki gün"
            className="rounded-(--radius-mod) border border-line-2 px-2 py-1.5 text-sm text-wait hover:border-navy hover:text-navy"
          >
            ‹
          </button>
          <input
            type="date"
            value={gun}
            max={BUGUN}
            onChange={(e) => gunSec(e.target.value)}
            className="num rounded-(--radius-mod) border border-line-2 px-2.5 py-1.5 text-sm outline-none focus:border-navy"
          />
          <button
            onClick={() => kaydir(1)}
            disabled={gun >= BUGUN}
            title="Sonraki gün"
            className="rounded-(--radius-mod) border border-line-2 px-2 py-1.5 text-sm text-wait hover:border-navy hover:text-navy disabled:opacity-30 disabled:hover:border-line-2"
          >
            ›
          </button>
          {gun !== BUGUN && (
            <button
              onClick={() => gunSec(BUGUN)}
              className="ml-1 rounded-(--radius-mod) bg-navy px-3 py-1.5 text-sm font-medium text-white"
            >
              Bugün
            </button>
          )}
        </div>
        {r.gonderildiMi ? (
          <span className="rounded-(--radius-mod) bg-ok/10 px-3 py-1.5 text-sm text-ok">
            ✓ Gönderildi{' '}
            {r.gonderimZamani &&
              new Date(r.gonderimZamani).toLocaleTimeString('tr-TR', {
                hour: '2-digit',
                minute: '2-digit',
              })}
          </span>
        ) : (
          <span className="rounded-(--radius-mod) bg-warn/10 px-3 py-1.5 text-sm text-warn">
            Taslak — şef onayı bekliyor
          </span>
        )}
      </div>

      {/* Gönderilmiş gün: ekranda MAİLDE GİDEN içerik durur, canlı derleme değil.
          İş emri sonradan düzeltilirse (K4) ikisi ayrışır ve şef hangisine
          baktığını bilmelidir. */}
      {r.arsiv && (
        <p className="mb-4 flex flex-wrap items-center gap-2 rounded-(--radius-mod) border border-line bg-ground p-3 text-sm text-wait">
          <span>
            Bu, raporun <strong>gönderildiği andaki hâli</strong> —{' '}
            {ben?.santiye.musteriAd ?? 'müşteriye'} giden içerik.
          </span>
          <button onClick={() => setCanli(true)} className="rounded-(--radius-mod) border border-navy-line bg-navy-bg px-2.5 py-1 text-xs font-medium text-navy hover:bg-navy hover:text-white">
            güncel veriyle yeniden derle
          </button>
        </p>
      )}
      {canli && r.gonderildiMi && (
        <p className="mb-4 rounded-(--radius-mod) border border-warn/30 bg-warn/5 p-3 text-sm text-warn">
          <strong>Güncel veriyle derlendi</strong> — bu içerik henüz gönderilmedi. Gönderirseniz
          o günün raporu bu hâliyle yeniden mail edilir.{' '}
          <button onClick={() => setCanli(false)} className="underline">
            gönderilen hâline dön
          </button>
        </p>
      )}

      {mesaj && (
        <p className="mb-4 rounded-(--radius-mod) bg-ok/10 p-3 text-sm text-ok">{mesaj}</p>
      )}
      {hata && <p className="mb-4 rounded-(--radius-mod) bg-crit/10 p-3 text-sm text-crit">{hata}</p>}

      {/*
        ⚠️ KÜNYE DÜZENLEME VE GÖNDERİM MÜŞTERİDE YOK (07.09.2026). Rapor
           Mod'un müşteriye verdiği belge; müşterinin kendisine rapor
           göndermesi ya da künyesini düzeltmesi anlamsız. Sunucu ikisini de
           `sefYetkisiVarMi` ile zaten reddediyor — burada düğmeyi çizmemek
           gerekçeyi görünür kılıyor, hata mesajı beklemek yerine.
      */}
      {!musteri && (
        <KunyeBlok
          r={r}
          onKaydedildi={yukle}
          // Gönderilmiş günün künyesi düzenlenebilir ama maili değiştirmez —
          // müşteriye ulaşması için yeniden gönderilmeli.
          gun={gun}
        />
      )}

      <Tablo
        baslik="Gün İçi İskele Kurulumları — otomatik"
        satirlar={r.kurulumlar}
        toplam={r.toplamKurulum}
      />
      <Tablo baslik="Gün İçi İskele Sökümleri" satirlar={r.sokumler} toplam={r.toplamSokum} />

      {/* Foto ekleri — kanıt zinciri (K8) */}
      <h3 className="mt-6 mb-2 text-sm font-semibold text-navy">
        Foto Ekleri — kanıt zinciri (her satır bir fotoğrafa bağlı)
      </h3>
      {r.fotolar.length === 0 ? (
        <p className="text-sm text-wait">Bu gün fotoğraf eklenmemiş.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {r.fotolar.map((f) => (
            <a
              key={f.fotoNo}
              href={`/api/fotolar/${f.fotoNo}`}
              target="_blank"
              rel="noreferrer"
              className="rounded-(--radius-mod) border border-line bg-surface shadow-(--shadow-mod) px-3 py-2 text-xs hover:border-navy"
            >
              <span className="num block font-semibold text-navy"><KameraIkon boyut={13} /> {f.fotoNo}</span>
              <span className="block text-wait">
                {f.isEmriNo ?? '—'} · {f.unite}
              </span>
            </a>
          ))}
        </div>
      )}

      {/* Eksik alan uyarısı — dürüst olmak gerekiyor */}
      {r.eksikAlanlar.length > 0 && (
        <p className="mt-5 rounded-(--radius-mod) bg-warn/10 p-3 text-xs leading-relaxed text-warn">
          <strong>Künyede {r.eksikAlanlar.length} alan boş:</strong>{' '}
          {r.eksikAlanlar.map((a) => ALAN_ADI[a] ?? a).join(', ')}. Bu alanlar sistemde
          otomatik üretilemiyor (nereden geleceği Bekir'e soruldu — SORULAR.md S8);
          rapor gönderilirse müşteriye <strong>“—”</strong> olarak gider.
        </p>
      )}

      {/* Alt not — projenin değer önermesinin tek cümlelik özeti */}
      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-(--radius-mod) border border-line bg-surface shadow-(--shadow-mod) p-4">
        <p className="text-sm text-wait">
          Aylık kümülatif: kurulum{' '}
          <strong className="num text-ink">{fmt(r.aylikKurulum)} m³</strong> · söküm{' '}
          <strong className="num text-ink">{fmt(r.aylikSokum)} m³</strong>
          <span className="text-xs"> (eşdeğer — m² değerleri ×4 ile çevrildi)</span>
          <br />
          <span className="text-xs">
            Eskiden elle Excel'e giriliyordu; artık günün tiklerinden derlenir.
          </span>
        </p>
        {/* Gönderilmiş bir gün YENİDEN gönderilebilir (düzeltme sonrası), ama
            bunun için önce güncel veriyle derlemesi gerekir — kazara aynı
            raporun ikinci kez gitmesini engeller. */}
        {!musteri && (!r.gonderildiMi || canli) && (
          <button
            disabled={gonderiliyor}
            onClick={() => void gonder()}
            className="rounded-(--radius-mod) bg-ok px-5 py-2.5 text-sm font-medium text-white disabled:opacity-40"
          >
            {gonderiliyor
              ? 'Gönderiliyor…'
              : r.gonderildiMi
                ? '✓ Yeniden gönder'
                : `✓ Onayla & Gönder${gun !== BUGUN ? ` (${gunEtiket(gun)})` : ''}`}
          </button>
        )}
      </div>
    </div>
  );
}



function Tablo({
  baslik,
  satirlar,
  toplam,
}: {
  baslik: string;
  satirlar: RaporSatir[];
  toplam: { m3: number; m2: number; esdegerM3: number };
}) {
  return (
    <section className="mb-5">
      <h3 className="mb-2 text-sm font-semibold text-navy">{baslik}</h3>
      <div className="overflow-x-auto rounded-(--radius-mod) border border-line bg-surface shadow-(--shadow-mod)">
        <table className="w-full text-sm">
          <thead className="border-b border-line text-left text-wait">
            <tr>
              <th className="px-3 py-2 font-medium">Sipariş No</th>
              <th className="px-3 py-2 font-medium">Onay No</th>
              <th className="px-3 py-2 font-medium">Talep Eden</th>
              <th className="px-3 py-2 font-medium">Tanım</th>
              <th className="px-3 py-2 font-medium">Birim</th>
              <th className="px-3 py-2 text-right font-medium">Metraj</th>
              <th className="px-3 py-2 font-medium">Harcanan</th>
              <th className="px-3 py-2 font-medium">Foto No</th>
            </tr>
          </thead>
          <tbody>
            {satirlar.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-wait">
                  Bu gün kayıt yok.
                </td>
              </tr>
            )}
            {satirlar.map((s, i) => (
              <tr key={i} className="border-b border-line/70 last:border-0">
                <td className="num px-3 py-2 font-medium text-navy">{s.siparisNo}</td>
                <td className="num px-3 py-2 text-ink-2">{s.iskeleOnayNo ?? '—'}</td>
                <td className="px-3 py-2">{s.talepEden}</td>
                <td className="px-3 py-2">{s.tanim}</td>
                <td className="px-3 py-2 text-wait">{s.talepEdenBirim}</td>
                <td className="num px-3 py-2 text-right">
                  {metrajGoster(s)}
                </td>
                {/* Gerçek raporda `3*2` biçimi — kişi × saat */}
                <td className="num px-3 py-2">{s.harcanan}</td>
                <td className="num px-3 py-2 text-wait">{s.fotoNo}</td>
              </tr>
            ))}
            <tr className="border-t-2 border-navy font-semibold">
              <td colSpan={4} className="px-3 py-2">
                Toplam
              </td>
              <td className="num px-3 py-2 text-right">{metrajYazi(toplam)}</td>
              <td colSpan={2} />
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}


/**
 * KÜNYE — sistemin oto-üretemediği alanlar (SORULAR.md S8).
 *
 * Bu blok, ekranda uzun süre "— (elle girilecek)" yazıp elle de girilemeyen
 * alanların karşılığı. Yazan ama yapılmayan bir vaat, olmayan bir alandan
 * daha kötüdür: şef doldurduğunu sanıp göndermiş olabilirdi.
 *
 * ⚠️ Ekipman ve tonaj ÖNCEKİ GÜNDEN devralınır (sunucu tarafında), hava ve
 *    personel devralınmaz — her gün değişen veriyi kopyalamak müşteriye sessizce
 *    yanlış bilgi göndermek olurdu.
 */
/** Raporun kapsadığı günün bir önceki günü — künye etiketinde gösterilir. */
function oncekiGun(isoGun: string): string {
  const d = new Date(`${isoGun}T12:00:00Z`); // öğle: gün kayması olmasın
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toLocaleDateString('tr-TR');
}

function KunyeBlok({
  r,
  gun,
  onKaydedildi,
}: {
  r: Rapor;
  gun: string;
  onKaydedildi: () => Promise<void> | void;
}) {
  const [duzenle, setDuzenle] = useState(false);
  const [k, setK] = useState<Kunye>(r.kunye);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [hata, setHata] = useState<string | null>(null);

  // Gün değişince (veya arşiv/canlı geçişinde) form tazelenmeli.
  useEffect(() => {
    setK(r.kunye);
    setDuzenle(false);
    setHata(null);
  }, [r.kunye, gun]);

  const yaz = <A extends keyof Kunye>(alan: A, deger: Kunye[A]) => setK({ ...k, [alan]: deger });

  async function kaydet() {
    setKaydediliyor(true);
    setHata(null);
    try {
      await api.patch('/rapor/kunye', { tarih: gun, ...k });
      setDuzenle(false);
      await onKaydedildi();
    } catch (e) {
      setHata(e instanceof Error ? e.message : 'Kaydedilemedi');
    } finally {
      setKaydediliyor(false);
    }
  }

  const gir =
    'w-full rounded-(--radius-mod) border border-line-2 px-2.5 py-1.5 text-sm outline-none focus:border-navy';
  const bos = (v: unknown) => v === null || v === undefined || v === '';

  const ekipmanMetni = k.ekipman.length ? k.ekipman.map((e) => `${e.ad} ${e.adet}`).join(' · ') : null;

  return (
    <section className="mb-5 overflow-hidden rounded-(--radius-mod) border border-line bg-surface shadow-(--shadow-mod)">
      <div className="flex items-center justify-between border-b border-line px-3 py-2">
        <h3 className="text-sm font-semibold text-navy">Künye</h3>
        {r.arsiv ? (
          <span className="text-xs text-wait">gönderilen hâli — düzenlenemez</span>
        ) : duzenle ? (
          <div className="flex items-center gap-2">
            <button
              onClick={() => void kaydet()}
              disabled={kaydediliyor}
              className="rounded-(--radius-mod) bg-navy px-3 py-1 text-xs font-medium text-white disabled:opacity-40"
            >
              {kaydediliyor ? 'Kaydediliyor…' : 'Kaydet'}
            </button>
            <button
              onClick={() => {
                setK(r.kunye);
                setDuzenle(false);
              }}
              className="rounded-(--radius-mod) px-2 py-1 text-xs font-medium text-muted hover:bg-surface-2 hover:text-ink"
            >
              Vazgeç
            </button>
          </div>
        ) : (
          <button onClick={() => setDuzenle(true)} className="rounded-(--radius-mod) border border-navy-line bg-navy-bg px-2.5 py-1 text-xs font-medium text-navy hover:bg-navy hover:text-white">
            Düzenle
          </button>
        )}
      </div>

      {hata && <p className="border-b border-line bg-crit/10 px-3 py-2 text-sm text-crit">{hata}</p>}

      <div className="grid grid-cols-2 gap-px bg-neutral-200 text-sm md:grid-cols-4">
        <Hucre etiket="İşveren" deger={r.isveren} />
        <Hucre etiket="Şantiye Şefi" deger={r.santiyeSefi} />

        <Hucre
          etiket="Hava"
          deger={k.hava}
          bosMetin="— (girilmedi)"
          alan={
            duzenle && (
              <input
                className={gir}
                value={k.hava}
                placeholder="Açık / Sert Rüzgarlı"
                onChange={(e) => yaz('hava', e.target.value)}
              />
            )
          }
        />

        <Hucre
          etiket="Sıcaklık"
          deger={bos(k.sicaklik) ? '' : `${k.sicaklik} C°`}
          bosMetin="— (girilmedi)"
          alan={
            duzenle && (
              <input
                className={`${gir} num`}
                type="number"
                value={k.sicaklik ?? ''}
                placeholder="8"
                onChange={(e) =>
                  yaz('sicaklik', e.target.value === '' ? null : Number(e.target.value))
                }
              />
            )
          }
        />

        <Hucre
          etiket="Personel"
          deger={bos(k.personel) ? '' : `${k.personel} kişi`}
          bosMetin="— (girilmedi)"
          alan={
            duzenle && (
              <input
                className={`${gir} num`}
                type="number"
                min={0}
                value={k.personel ?? ''}
                placeholder="8"
                onChange={(e) =>
                  yaz('personel', e.target.value === '' ? null : Number(e.target.value))
                }
              />
            )
          }
        />

        <Hucre
          etiket="İskele Malzemesi"
          deger={bos(k.malzemeTonaj) ? '' : `${k.malzemeTonaj} ton`}
          bosMetin="— (girilmedi)"
          alan={
            duzenle && (
              <input
                className={`${gir} num`}
                type="number"
                min={0}
                value={k.malzemeTonaj ?? ''}
                placeholder="220"
                onChange={(e) =>
                  yaz('malzemeTonaj', e.target.value === '' ? null : Number(e.target.value))
                }
              />
            )
          }
        />

        <Hucre
          etiket="Ekipman Parkı"
          deger={ekipmanMetni ?? ''}
          bosMetin="— (girilmedi)"
          genislik="yarim"
          alan={duzenle && <EkipmanDuzenle liste={k.ekipman} onDegisti={(l) => yaz('ekipman', l)} />}
        />

        {/* TÜRETİLMİŞ — elle girilmez, rutin kontrollerden gelir.
            Satırı TAM kaplar: eksik hücre bırakmak ızgarada gri boşluk üretiyordu. */}
        <Hucre
          etiket="Ünite Kontrolleri (otomatik)"
          deger={r.uniteKontrol.map((u) => `${u.unite}: ${u.adet}`).join(' · ')}
          bosMetin="— bugün haftalık kontrol yok"
          genislik="tam"
        />

        {/* TÜRETİLMİŞ — teslim ve haftalık kontrol kayıtlarından, son yedi gün. */}
        <Hucre
          etiket="Son Bir Hafta Güncellenen İskele (otomatik)"
          deger={
            r.haftalikGuncelleme.length
              ? `${r.haftalikGuncelleme
                  .map((h) => `${gunEtiket(h.tarih)}: ${h.adet}`)
                  .join(' · ')}  —  Toplam ${r.haftalikToplam}`
              : ''
          }
          bosMetin="— kayıt yok"
          genislik="tam"
        />
      </div>

      {/*
        BUGÜNKÜ PERSONEL — öneriyle DOLU açılır.
        ⚠️ Öneri günün faz ekiplerinden türetilir ve KÜNYEYE YAZILMAZ: şef
           bakmadan "girilmiş" sayılsaydı, türetilmiş bir tahmin elle
           doğrulanmış veri gibi müşteriye giderdi. Alan boşsa öneri
           gösterilir; şef dokunduğu an kendi metni geçerli olur.
      */}
      <div className="border-t border-line px-3 py-2">
        <div className="text-xs text-wait">
          Personel — <span className="num">bugün</span>
        </div>
        {duzenle ? (
          <>
            <textarea
              className={`${gir} mt-1 min-h-20`}
              value={k.bugunkuPersonel}
              placeholder="Şantiye Şefi: … · Usta: …"
              onChange={(e) => yaz('bugunkuPersonel', e.target.value)}
            />
            {!k.bugunkuPersonel && r.personelOneri && (
              <button
                type="button"
                onClick={() => yaz('bugunkuPersonel', r.personelOneri)}
                className="mt-1 text-left text-xs text-navy hover:underline"
              >
                Bugün çalışanlardan doldur: <span className="text-wait">{r.personelOneri}</span>
              </button>
            )}
          </>
        ) : (
          <div className={k.bugunkuPersonel ? 'whitespace-pre-wrap' : 'text-wait'}>
            {k.bugunkuPersonel || '— (girilmedi)'}
          </div>
        )}
      </div>

      {/*
        GÜN İÇİ ÖNEMLİ BİLDİRİM VE NOTLAR — Excel şablonunda ayrı bir bölüm.
        ⚠️ ZORUNLU DEĞİL: eksik alan uyarısına girmiyor ve boşsa müşteriye giden
           belgede bölüm hiç açılmıyor. Boş bir başlık, doldurulacak bir yer
           varmış izlenimi verirdi.
      */}
      <div className="border-t border-line px-3 py-2">
        <div className="text-xs text-wait">Gün içi önemli bildirim ve notlar</div>
        {duzenle ? (
          <textarea
            className={`${gir} mt-1 min-h-20`}
            value={k.notlar}
            placeholder="Duruş, kaza, ziyaret, olağandışı durum… (boş bırakılabilir)"
            onChange={(e) => yaz('notlar', e.target.value)}
          />
        ) : (
          <div className={k.notlar ? 'whitespace-pre-wrap' : 'text-wait'}>
            {k.notlar || '— (yok)'}
          </div>
        )}
      </div>

      <div className="border-t border-line px-3 py-2">
        {/* Tarih ETİKETTE yazıyor — alan eskiden "yarınki plan"dı ve hangi günü
            anlattığı ekranda görünmüyordu. Karışıklık oradan doğmuştu. */}
        <div className="text-xs text-wait">
          Personel — <span className="num">{oncekiGun(r.tarih)}</span>
        </div>
        {duzenle ? (
          <textarea
            className={`${gir} mt-1 min-h-20`}
            value={k.dunkuPersonel}
            placeholder="Bedirhan Malkoç, İlyas Apiş, Burak Demir…"
            onChange={(e) => yaz('dunkuPersonel', e.target.value)}
          />
        ) : (
          <div className={k.dunkuPersonel ? 'whitespace-pre-wrap' : 'text-wait'}>
            {k.dunkuPersonel || '— (girilmedi)'}
          </div>
        )}
      </div>
    </section>
  );
}

/**
 * Künye hücresi.
 *
 * ⚠️ `alan` DÜZENLEME GİRDİSİDİR ve yalnız düzenleme modunda verilir.
 *    Görüntüleme modunda çağıran taraf `duzenle && <input/>` yazıyor; bu ifade
 *    `false` üretir. Burada `children ?? deger` yazılırsa `false ?? x` → `false`
 *    olur (`??` yalnız null/undefined'da devreye girer) ve hücre BOŞ çizilir.
 *    Gerçek değer varken bile görünmez — girilmiş 220 ton bu yüzden kaybolmuştu.
 *    Bu yüzden açıkça `alan ? alan : değer` kontrolü yapılıyor.
 */
function Hucre({
  etiket,
  deger,
  bosMetin,
  genislik,
  alan,
}: {
  etiket: string;
  deger: string;
  bosMetin?: string;
  /** Izgarada kaç sütun kaplasın. Boş hücre bırakmamak için satırlar tam dolmalı. */
  genislik?: 'yarim' | 'tam';
  alan?: ReactNode;
}) {
  const span =
    genislik === 'tam'
      ? 'col-span-2 md:col-span-4'
      : genislik === 'yarim'
        ? 'col-span-2'
        : '';
  return (
    <div className={`bg-white px-3 py-2 ${span}`}>
      <div className="text-xs text-wait">{etiket}</div>
      {alan ? (
        alan
      ) : (
        <div className={deger ? 'text-ink' : 'text-wait'}>{deger || bosMetin || '—'}</div>
      )}
    </div>
  );
}

/** Ekipman parkı — ad + adet satırları. Gerçek raporda "Binek 1, Hidrolik 2…". */
function EkipmanDuzenle({
  liste,
  onDegisti,
}: {
  liste: Ekipman[];
  onDegisti: (l: Ekipman[]) => void;
}) {
  const gir = 'rounded-(--radius-mod) border border-line-2 px-2 py-1 text-sm outline-none focus:border-navy';
  return (
    <div className="mt-1 space-y-1">
      {liste.map((e, i) => (
        <div key={i} className="flex items-center gap-1">
          <input
            className={`${gir} min-w-0 flex-1`}
            value={e.ad}
            placeholder="Forklift"
            onChange={(ev) =>
              onDegisti(liste.map((x, j) => (j === i ? { ...x, ad: ev.target.value } : x)))
            }
          />
          <input
            className={`${gir} num w-14`}
            type="number"
            min={0}
            value={e.adet}
            onChange={(ev) =>
              onDegisti(liste.map((x, j) => (j === i ? { ...x, adet: Number(ev.target.value) } : x)))
            }
          />
          <button
            onClick={() => onDegisti(liste.filter((_, j) => j !== i))}
            className="px-1.5 text-sm text-wait hover:text-crit"
            title="Kaldır"
          >
            <KapatIkon boyut={16} />
          </button>
        </div>
      ))}
      <button
        onClick={() => onDegisti([...liste, { ad: '', adet: 1 }])}
        className="rounded-(--radius-mod) border border-navy-line bg-navy-bg px-2.5 py-1 text-xs font-medium text-navy hover:bg-navy hover:text-white"
      >
        + ekipman ekle
      </button>
    </div>
  );
}
